import {Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ImportModuleAdminEventRetryResult, ImportModuleRunResult} from '../../../core/definitions/import-module';
import {createdRevisionKindLabel} from '../created-revision';
import {downloadCsv} from '../csv-download';
import {MEDIA_VALUES_TITLE_KEY} from '../live-preview/live-preview';

/** An unresolved concept value with its target field's label. */
export interface UnresolvedConceptValueView {
  entry: ImportModuleRunResult['unresolved_concept_values'][number];
  label: string;
}

/** A card the run wrote nothing for on a row, with the card's title;
 * `null` for a key no card has. */
export interface SkippedGroupView {
  entry: NonNullable<ImportModuleRunResult['skipped_groups']>[number];
  title: string | null;
}

/**
 * A finished import run's result: the counts, every list of values and
 * rows the run could not use, the media folder's files that matched no
 * row, the outcome of the latest admin-event retry, the reindex hint, and
 * the retry and download actions for failed rows.
 */
@Component({
  selector: 'app-import-run-result',
  templateUrl: './import-run-result.component.html',
  styleUrls: ['./import-run-result.component.scss'],
  standalone: false,
})
export class ImportRunResultComponent implements OnChanges {
  @Input({required: true}) runResult!: ImportModuleRunResult;
  /** A run or retry is in progress; the retry buttons are disabled. */
  @Input() running = false;
  /** The latest admin-event retry's outcome, shown under the failures. */
  @Input() adminEventRetryResult: ImportModuleAdminEventRetryResult | null = null;
  /** The source column the shown run was started with to join media on,
   * `null` when it joined none or its spec is not at hand. */
  @Input() mediaJoinColumn: string | null = null;
  /** The source file's name, which the failed-rows CSV is named after. */
  @Input() fileName = '';
  /** A target field's display label, from its id. */
  @Input({required: true}) resolveLabel!: (fieldId: string) => string;
  /** The title of the flat group card or admin-event card a key belongs
   * to, `null` for a key no card has. */
  @Input({required: true}) resolveCardTitle!: (key: string) => string | null;

  /** Asks for the clean-failure rows to be run again. */
  @Output() retryFailedRows = new EventEmitter<void>();
  /** Asks for the listed admin-event failures to be written again. */
  @Output() retryAdminEvents = new EventEmitter<void>();

  /** Labels for the run's unresolved target fields, which the server names
   * only by id. Built once per result. */
  unresolvedFieldLabels: string[] = [];
  /** The run's unresolved concept values, each with its target field's
   * label. Built once per result. */
  unresolvedConceptValues: UnresolvedConceptValueView[] = [];
  /** The cards the run wrote nothing for, each with its title. Built once
   * per result. */
  skippedGroups: SkippedGroupView[] = [];
  /** The title a value meant for the linked media is labelled with. */
  readonly mediaValuesTitleKey = MEDIA_VALUES_TITLE_KEY;

  private readonly translate = inject(TranslateService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['runResult'] || changes['resolveLabel']) {
      this.unresolvedFieldLabels = (this.runResult.unresolved_fields ?? []).map(id => this.resolveLabel(id));
      this.unresolvedConceptValues = this.runResult.unresolved_concept_values.map(
        entry => ({entry, label: this.resolveLabel(entry.target_field)}));
    }
    if (changes['runResult'] || changes['resolveCardTitle']) {
      this.skippedGroups = this.buildSkippedGroups();
    }
  }

  /** A skipped value's reasons, translated and comma-joined. `reasons` are
   * `TRANS__IMPORT__SKIP_REASON__*` keys, or a raw `error.*` keyword with
   * no key, which `instant` echoes. */
  skippedValueReasons(reasons: string[]): string {
    return reasons.map(key => this.translate.instant(key)).join(', ');
  }

  /** The true count behind a capped run-summary bucket, its `*_total`
   * field. `clean_failures`, `orphan_failures`, `admin_event_failures`,
   * `media_field_failures` and `skipped_field_values` list only the first
   * entries, so a header or a retry button counts from the total, not from
   * the shown list. */
  cleanFailuresTotal(): number {
    return this.runResult.clean_failures_total ?? 0;
  }

  orphanFailuresTotal(): number {
    return this.runResult.orphan_failures_total ?? 0;
  }

  adminEventFailuresTotal(): number {
    return this.runResult.admin_event_failures_total ?? 0;
  }

  skippedFieldValuesTotal(): number {
    return this.runResult.skipped_field_values_total ?? 0;
  }

  mediaFieldFailuresTotal(): number {
    return this.runResult.media_field_failures_total ?? this.runResult.media_field_failures?.length ?? 0;
  }

  /** Whether the media block shows: the run was started with a join
   * column, or its result carries media counts. A run without a join and a
   * join run that linked nothing send the same zero counts, so the join
   * column tells them apart. */
  hasMedia(): boolean {
    const r = this.runResult;
    return !!(this.mediaJoinColumn || r.media_linked || r.media_files_linked || r.media_files_without_row_total != null
      || r.media_field_failures_total || r.media_field_failures?.length
      || this.mediaUnmatchedTotal() || this.mediaAmbiguousTotal()
      || this.mediaPartlyLinkedTotal() || this.mediaAlreadyLinkedTotal() || this.mediaSharedFileTotal());
  }

  /** The true count behind the capped `skipped_groups`: its total, or the
   * list's own length for a summary that carries no total. */
  skippedGroupsTotal(): number {
    return this.runResult.skipped_groups_total ?? this.runResult.skipped_groups?.length ?? 0;
  }

  /** The true count behind a capped media list: its `*_total` field, or the
   * list's own length for a summary that carries no total. */
  mediaUnmatchedTotal(): number {
    return this.runResult.media_unmatched_total ?? this.runResult.media_unmatched?.length ?? 0;
  }

  mediaAmbiguousTotal(): number {
    return this.runResult.media_ambiguous_total ?? this.runResult.media_ambiguous?.length ?? 0;
  }

  mediaPartlyLinkedTotal(): number {
    return this.runResult.media_partly_linked_total ?? this.runResult.media_partly_linked?.length ?? 0;
  }

  mediaAlreadyLinkedTotal(): number {
    return this.runResult.media_already_linked_total ?? this.runResult.media_already_linked?.length ?? 0;
  }

  mediaSharedFileTotal(): number {
    return this.runResult.media_shared_file_total ?? this.runResult.media_shared_file?.length ?? 0;
  }

  /** How many concept-list terms the run created for values that matched
   * nothing, across every list. */
  conceptsCreatedTotal(): number {
    return (this.runResult.concepts_created ?? []).reduce((sum, entry) => sum + entry.count, 0);
  }

  /** How many of a capped bucket's entries are not in the shown list, for
   * the "and N more" line under it; 0 (falsy, so the `@if` around it stays
   * closed) once every entry is shown. */
  droppedCount(total: number, shown: number): number {
    return Math.max(0, total - shown);
  }

  /** How many created date or user values the run could not apply: the
   * sum of the entries' `count`, not the number of distinct values. */
  get createdRevisionUnresolvedTotal(): number {
    return (this.runResult.created_revision_unresolved ?? []).reduce((sum, entry) => sum + entry.count, 0);
  }

  /** The target label for a `created_revision_unresolved` entry's `kind`. */
  createdRevisionKindLabel(kind: string): string {
    return createdRevisionKindLabel(kind, this.translate);
  }

  /** Saves a CSV of the shown failed rows' raw source values, clean and
   * orphan failures alike, for an operator to fix in a spreadsheet and
   * upload again as a new source. Only the listed entries are in it, so a
   * run with more failures than the lists show exports its first batch. */
  downloadFailedRowsCsv(): void {
    const failures = [
      ...(this.runResult.clean_failures ?? []),
      ...(this.runResult.orphan_failures ?? []),
    ];
    if (!failures.length) {
      return;
    }
    const columns = Array.from(new Set(failures.flatMap(f => Object.keys(f.values))));
    downloadCsv(`${this.fileName || 'raw_import'}_failed_rows.csv`, [
      columns,
      ...failures.map(f => columns.map(c => f.values[c])),
    ]);
  }

  /** `skipped_groups` with each card's title; each key is resolved once. */
  private buildSkippedGroups(): SkippedGroupView[] {
    const titles = new Map<string, string | null>();
    const title = (key: string): string | null => {
      if (!titles.has(key)) {
        titles.set(key, this.resolveCardTitle(key));
      }
      return titles.get(key)!;
    };
    return (this.runResult.skipped_groups ?? []).map(entry => ({entry, title: title(entry.card)}));
  }
}
